import { debugLog } from 'shared/logger/debugLog';
import type { ExecutionGate } from './gates/ExecutionGate';
import { GateWaitQueue } from './gates/GateWaitQueue';

export type RequestSlot = { id: string; signal: AbortSignal };

export const REQUEST_SUSPENDER_TIMEOUT_MS = 2_000;

export class RequestSuspender implements ExecutionGate {
  private suspendCount = 0;
  private activeRequests = new Map<string, AbortController>();
  private readonly waitQueue = new GateWaitQueue();
  private lastSuspendedAt: number | null = null;

  /**
   * Nested suspend/resume uses a counter so an inner lifecycle (e.g. project
   * flush) cannot resume the gate while an outer one still needs it closed.
   *
   * `abortAll()` runs only on 0→1. That is safe only because `register()` /
   * `registerWhenReady()` / `canExecute()` refuse new slots while
   * `suspendCount > 0`. Do not add retry paths that bypass those gates.
   */
  suspend(): void {
    const closing = this.suspendCount === 0;
    this.suspendCount += 1;

    if (closing) {
      this.lastSuspendedAt = Date.now();
      this.abortAll();
    }

    debugLog('RequestSuspender: Suspending operations', {
      suspendCount: this.suspendCount,
      activeRequests: this.activeRequests.size,
    });
  }

  /**
   * Opens the gate only when the matching suspend count returns to 0.
   * Extra resume while already open is a no-op (count stays 0).
   *
   * Waiters registered during `notifyAll` in this same turn are not in the
   * spliced batch and wait for a later wake (GateWaitQueue).
   */
  resume(): void {
    if (this.suspendCount === 0) {
      return;
    }

    this.suspendCount -= 1;
    if (this.suspendCount > 0) {
      debugLog('RequestSuspender: Nested resume, still suspended', {
        suspendCount: this.suspendCount,
      });
      return;
    }

    const suspendedForMs =
      this.lastSuspendedAt != null
        ? Date.now() - this.lastSuspendedAt
        : null;
    debugLog('RequestSuspender: Resuming operations', {
      suspendedForMs,
      activeRequests: this.activeRequests.size,
    });
    this.lastSuspendedAt = null;
    this.waitQueue.notifyAll();
  }

  isBlocked(): boolean {
    return this.suspendCount > 0;
  }

  canExecute(): boolean {
    return this.suspendCount === 0;
  }

  isSuspended(): boolean {
    return this.suspendCount > 0;
  }

  async waitUntilOpen(timeoutMs?: number): Promise<boolean> {
    return this.waitQueue.waitUntilOpen(
      () => this.suspendCount === 0,
      timeoutMs
    );
  }

  /**
   * Barrier helper: waits until `resume()` is called (or timeout).
   * Used to avoid throwing/dropping requests during short project switches / flush.
   */
  async waitUntilResumed(
    timeoutMs = REQUEST_SUSPENDER_TIMEOUT_MS
  ): Promise<boolean> {
    return this.waitUntilOpen(timeoutMs);
  }

  register(): RequestSlot {
    if (this.suspendCount > 0) {
      throw new Error('RequestSuspender: cannot register while suspended');
    }
    return this.registerInternal();
  }

  /**
   * Waits until SW is not suspended, then registers an active request slot
   * (abort signal tracked for suspend/logout). Returns null on timeout.
   * Unlike register(), does not throw while suspended — it waits for resume().
   */
  async registerWhenReady(
    timeoutMs = REQUEST_SUSPENDER_TIMEOUT_MS
  ): Promise<RequestSlot | null> {
    const slot = await this.waitQueue.waitForCallback(
      () => (this.suspendCount > 0 ? null : this.registerInternal()),
      timeoutMs
    );

    if (!slot) {
      debugLog('RequestSuspender: registerWhenReady timeout', {
        timeoutMs,
        suspendCount: this.suspendCount,
      });
    }

    return slot;
  }

  private registerInternal(): RequestSlot {
    const id = crypto.randomUUID();
    const controller = new AbortController();
    this.activeRequests.set(id, controller);

    return { id, signal: controller.signal };
  }

  unregister(id: string): void {
    if (!this.activeRequests.delete(id)) {
      debugLog('RequestSuspender: unregister unknown id', id);
    }
  }

  abortAll(): void {
    if (this.activeRequests.size === 0) {
      return;
    }

    this.activeRequests.forEach((controller) => {
      try {
        controller.abort();
      } catch (error) {
        debugLog('RequestSuspender: Error aborting request', error);
      }
    });
    this.activeRequests.clear();
  }

  getActiveCount(): number {
    return this.activeRequests.size;
  }

  /**
   * Hard open: sets count to 0 even if nested suspends are outstanding.
   * Unlike `resume()`, this is teardown, not a matching unlock.
   */
  reset(): void {
    this.suspendCount = 0;
    this.lastSuspendedAt = null;
    this.abortAll();
    this.waitQueue.notifyAll();
    this.waitQueue.clear();
  }
}

export const requestSuspender = new RequestSuspender();
